// Helpers para Sites com dados Goodwe
import React from 'react';
import {
    ThunderboltOutlined,
    ClockCircleOutlined,
    DisconnectOutlined,
    WarningOutlined,
    CloseCircleOutlined,
    QuestionCircleOutlined,
    HomeOutlined,
    BankOutlined,
    DatabaseOutlined,
    EnvironmentOutlined,
} from '@ant-design/icons';

/**
 * Retorna cor baseada no status Goodwe
 * @param {number} status - Status Goodwe (-3 a 1)
 * @returns {string} Cor do Ant Design
 */
export const getStatusColor = (status) => {
  switch (parseInt(status, 10)) {
    case 1:  return 'green';    // Funcionando / Gerando
    case 0:  return 'blue';     // Aguardando
    case 2:  return 'orange';   // Alerta
    case 3:  return 'cyan';     // Construindo
    case -1: return 'default';  // Offline
    case -2: return 'purple';   // Sem Dispositivo
    case -3: return 'red';      // Falha
    default: return 'default';
  }
};

/**
 * Retorna texto traduzido do status
 * 1=GERANDO (produzindo agora), 0=AGUARDANDO (conectado, sem sol), -1=OFFLINE, -2=SEM DISPOSITIVO, -3=FALHA
 * AGUARDANDO com dailyGeneration>0 = gerou hoje mas não está gerando no momento (ex.: noite)
 */
export const getStatusText = (status) => {
  switch (parseInt(status, 10)) {
    case 1:  return 'GERANDO';
    case 0:  return 'AGUARDANDO';
    case 2:  return 'ALERTA';
    case 3:  return 'CONSTRUINDO';
    case -1: return 'OFFLINE';
    case -2: return 'SEM DISPOSITIVO';
    case -3: return 'FALHA';
    default: return 'DESCONHECIDO';
  }
};

/**
 * Retorna ícone baseado no status
 */
export const getStatusIcon = (status) => {
  switch (parseInt(status, 10)) {
    case 1:  return <ThunderboltOutlined />;    // Gerando
    case 0:  return <ClockCircleOutlined />;    // Aguardando
    case 2:  return <WarningOutlined />;        // Alerta
    case 3:  return <HomeOutlined />;           // Construindo
    case -1: return <DisconnectOutlined />;     // Offline
    case -2: return <QuestionCircleOutlined />; // Sem Dispositivo
    case -3: return <CloseCircleOutlined />;    // Falha
    default: return <QuestionCircleOutlined />;
  }
};

/**
 * Retorna ícone baseado no tipo de planta
 */
export const getPlantTypeIcon = (type) => {
  switch (type) {
    case 'residential':
      return <HomeOutlined style={{ color: '#1890ff' }} />;
    case 'commercial':
      return <BankOutlined style={{ color: '#722ed1' }} />;
    case 'storage':
      return <DatabaseOutlined style={{ color: '#52c41a' }} />;
    case 'ground-mounted':
      return <ThunderboltOutlined style={{ color: '#FFB100' }} />;
    default:
      return <EnvironmentOutlined />;
  }
};

/**
 * Retorna label traduzida do tipo de planta
 */
export const getPlantTypeLabel = (type) => {
  switch (type) {
    case 'residential':
      return 'Residencial';
    case 'commercial':
      return 'Comercial';
    case 'storage':
      return 'Armazenamento';
    case 'ground-mounted':
      return 'Solo';
    default:
      return '-';
  }
};

/**
 * Retorna ícone de bateria baseado no SOC
 */
export const getBatteryIcon = (soc) => {
  if (!soc) return <ThunderboltOutlined style={{ color: '#8c8c8c' }} />;
  if (soc >= 80) return <ThunderboltOutlined style={{ color: '#52c41a' }} />;
  if (soc >= 50) return <ThunderboltOutlined style={{ color: '#1890ff' }} />;
  if (soc >= 20) return <ThunderboltOutlined style={{ color: '#faad14' }} />;
  return <ThunderboltOutlined style={{ color: '#ff4d4f' }} />;
};

/**
 * Retorna ícone de status da bateria (carregando/descarregando)
 * @param {number} status - 1: Standby, 2: Discharging, 3: Charging
 */
export const getBatteryStatusIcon = (status) => {
  if (status === 3) return <ThunderboltOutlined style={{ color: '#52c41a' }} />; // Charging
  if (status === 2) return <ThunderboltOutlined style={{ color: '#ff4d4f' }} />; // Discharging
  return <ThunderboltOutlined style={{ color: '#8c8c8c' }} />; // Standby
};

/**
 * Retorna texto do status da bateria
 */
export const getBatteryStatusText = (status) => {
  if (status === 3) return 'Carregando';
  if (status === 2) return 'Descarregando';
  return 'Standby';
};

/**
 * Formata potência em W para kW com precisão
 */
export const formatPower = (watts, precision = 1) => {
  if (!watts && watts !== 0) return '-';
  return `${(watts / 1000).toFixed(precision)} kW`;
};

/**
 * Formata energia em kWh
 */
export const formatEnergy = (kwh, precision = 1) => {
  if (!kwh && kwh !== 0) return '-';
  return `${kwh.toFixed(precision)} kWh`;
};

/**
 * Formata valor monetário
 */
export const formatCurrency = (value, precision = 2) => {
  if (!value && value !== 0) return '-';
  return `R$ ${value.toFixed(precision)}`;
};
